/**
 * Ritual Mutations Hook
 * Handles create and update operations for rituals with proper cache invalidation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { CreateRitualRequest, UpdateRitualData } from "../services/ritual.service"
import { createRitual, updateRitual } from "../services/ritual.service"

export function useCreateRitual() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateRitualRequest) => createRitual(data),
    onSuccess: () => {
      // Invalidate user's teachings (created rituals)
      queryClient.invalidateQueries({
        queryKey: ["user-ritual-teachings"],
      })

      // Invalidate all community rituals
      queryClient.invalidateQueries({
        queryKey: ["community-rituals"],
      })

      // Invalidate profile statistics
      queryClient.invalidateQueries({
        queryKey: ["profileStatistics"],
      })
    },
  })
}

export function useUpdateRitual(ritualId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<UpdateRitualData> | FormData) => {
      return updateRitual(ritualId, data)
    },
    onSuccess: () => {
      // Invalidate the specific ritual detail
      queryClient.invalidateQueries({
        queryKey: ["ritual", ritualId],
      })

      // Invalidate all community rituals (ritual might belong to a community)
      queryClient.invalidateQueries({
        queryKey: ["community-rituals"],
      })

      // Invalidate user's teachings and practices
      queryClient.invalidateQueries({
        queryKey: ["user-ritual-teachings"],
      })
      queryClient.invalidateQueries({
        queryKey: ["user-ritual-practices"],
      })
    },
  })
}
